import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Col, Form, FormGroup, Input, InputGroup, Row, Table } from 'reactstrap';
import { JhiItemCount, JhiPagination, Translate, getPaginationState, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons';
import { ASC, DESC, ITEMS_PER_PAGE, SORT } from 'app/shared/util/pagination.constants';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities, searchEntities } from './tenant-association.reducer';

export const TenantAssociation = () => {
  const dispatch = useAppDispatch();

  const pageLocation = useLocation();
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(getPaginationState(pageLocation, ITEMS_PER_PAGE, 'id'), pageLocation.search),
  );

  const tenantAssociationList = useAppSelector(state => state.tenantAssociation.entities);
  const loading = useAppSelector(state => state.tenantAssociation.loading);
  const totalItems = useAppSelector(state => state.tenantAssociation.totalItems);

  const getAllEntities = () => {
    if (search) {
      dispatch(
        searchEntities({
          query: search,
          page: paginationState.activePage - 1,
          size: paginationState.itemsPerPage,
          sort: `${paginationState.sort},${paginationState.order}`,
        }),
      );
    } else {
      dispatch(
        getEntities({
          page: paginationState.activePage - 1,
          size: paginationState.itemsPerPage,
          sort: `${paginationState.sort},${paginationState.order}`,
        }),
      );
    }
  };

  const startSearching = e => {
    if (search) {
      setPaginationState({
        ...paginationState,
        activePage: 1,
      });
      dispatch(
        searchEntities({
          query: search,
          page: paginationState.activePage - 1,
          size: paginationState.itemsPerPage,
          sort: `${paginationState.sort},${paginationState.order}`,
        }),
      );
    }
    e.preventDefault();
  };

  const clear = () => {
    setSearch('');
    setPaginationState({
      ...paginationState,
      activePage: 1,
    });
    dispatch(getEntities({}));
  };

  const handleSearch = event => setSearch(event.target.value);

  const sortEntities = () => {
    getAllEntities();
    const endURL = `?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`;
    if (pageLocation.search !== endURL) {
      navigate(`${pageLocation.pathname}${endURL}`);
    }
  };

  useEffect(() => {
    sortEntities();
  }, [paginationState.activePage, paginationState.order, paginationState.sort, search]);

  useEffect(() => {
    const params = new URLSearchParams(pageLocation.search);
    const page = params.get('page');
    const sort = params.get(SORT);
    if (page && sort) {
      const sortSplit = sort.split(',');
      setPaginationState({
        ...paginationState,
        activePage: +page,
        sort: sortSplit[0],
        order: sortSplit[1],
      });
    }
  }, [pageLocation.search]);

  const sort = p => () => {
    setPaginationState({
      ...paginationState,
      order: paginationState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handlePagination = currentPage =>
    setPaginationState({
      ...paginationState,
      activePage: currentPage,
    });

  const handleSyncList = () => {
    sortEntities();
  };

  const getSortIconByFieldName = (fieldName: string) => {
    const sortFieldName = paginationState.sort;
    const order = paginationState.order;
    if (sortFieldName !== fieldName) {
      return faSort;
    }
    return order === ASC ? faSortUp : faSortDown;
  };

  return (
    <div>
      <h2 id="tenant-association-heading" data-cy="TenantAssociationHeading">
        <Translate contentKey="tinyMedicRestApp.tenantAssociation.home.title">Tenant Associations</Translate>
        <div className="d-flex justify-content-end">
          <Button className="me-2" color="info" onClick={handleSyncList} disabled={loading}>
            <FontAwesomeIcon icon="sync" spin={loading} />{' '}
            <Translate contentKey="tinyMedicRestApp.tenantAssociation.home.refreshListLabel">Refresh List</Translate>
          </Button>
          <Link
            to="/tenant-association/new"
            className="btn btn-primary jh-create-entity"
            id="jh-create-entity"
            data-cy="entityCreateButton"
          >
            <FontAwesomeIcon icon="plus" />
            &nbsp;
            <Translate contentKey="tinyMedicRestApp.tenantAssociation.home.createLabel">Create new Tenant Association</Translate>
          </Link>
        </div>
      </h2>
      <Row>
        <Col sm="12">
          <Form onSubmit={startSearching}>
            <FormGroup>
              <InputGroup>
                <Input
                  type="text"
                  name="search"
                  defaultValue={search}
                  onChange={handleSearch}
                  placeholder={translate('tinyMedicRestApp.tenantAssociation.home.search')}
                />
                <Button className="input-group-addon">
                  <FontAwesomeIcon icon="search" />
                </Button>
                <Button type="reset" className="input-group-addon" onClick={clear}>
                  <FontAwesomeIcon icon="trash" />
                </Button>
              </InputGroup>
            </FormGroup>
          </Form>
        </Col>
      </Row>
      <div className="table-responsive">
        {tenantAssociationList && tenantAssociationList.length > 0 ? (
          <Table responsive>
            <thead>
              <tr>
                <th className="hand" onClick={sort('id')}>
                  <Translate contentKey="tinyMedicRestApp.tenantAssociation.id">Id</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('id')} />
                </th>
                <th className="hand" onClick={sort('associatedDate')}>
                  <Translate contentKey="tinyMedicRestApp.tenantAssociation.associatedDate">Associated Date</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('associatedDate')} />
                </th>
                <th className="hand" onClick={sort('internal')}>
                  <Translate contentKey="tinyMedicRestApp.tenantAssociation.internal">Internal</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('internal')} />
                </th>
                <th className="hand" onClick={sort('leavingDate')}>
                  <Translate contentKey="tinyMedicRestApp.tenantAssociation.leavingDate">Leaving Date</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('leavingDate')} />
                </th>
                <th className="hand" onClick={sort('openingTime')}>
                  <Translate contentKey="tinyMedicRestApp.tenantAssociation.openingTime">Opening Time</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('openingTime')} />
                </th>
                <th className="hand" onClick={sort('closeTime')}>
                  <Translate contentKey="tinyMedicRestApp.tenantAssociation.closeTime">Close Time</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('closeTime')} />
                </th>
                <th className="hand" onClick={sort('createdDtm')}>
                  <Translate contentKey="tinyMedicRestApp.tenantAssociation.createdDtm">Created Dtm</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('createdDtm')} />
                </th>
                <th className="hand" onClick={sort('createdBy')}>
                  <Translate contentKey="tinyMedicRestApp.tenantAssociation.createdBy">Created By</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('createdBy')} />
                </th>
                <th className="hand" onClick={sort('modifiedBy')}>
                  <Translate contentKey="tinyMedicRestApp.tenantAssociation.modifiedBy">Modified By</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('modifiedBy')} />
                </th>
                <th className="hand" onClick={sort('leftAssociation')}>
                  <Translate contentKey="tinyMedicRestApp.tenantAssociation.leftAssociation">Left Association</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('leftAssociation')} />
                </th>
                <th className="hand" onClick={sort('active')}>
                  <Translate contentKey="tinyMedicRestApp.tenantAssociation.active">Active</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('active')} />
                </th>
                <th className="hand" onClick={sort('cursor')}>
                  <Translate contentKey="tinyMedicRestApp.tenantAssociation.cursor">Cursor</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('cursor')} />
                </th>
                <th className="hand" onClick={sort('lastUpdated')}>
                  <Translate contentKey="tinyMedicRestApp.tenantAssociation.lastUpdated">Last Updated</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('lastUpdated')} />
                </th>
                <th className="hand" onClick={sort('microSec')}>
                  <Translate contentKey="tinyMedicRestApp.tenantAssociation.microSec">Micro Sec</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('microSec')} />
                </th>
                <th className="hand" onClick={sort('timestampEntry')}>
                  <Translate contentKey="tinyMedicRestApp.tenantAssociation.timestampEntry">Timestamp Entry</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('timestampEntry')} />
                </th>
                <th className="hand" onClick={sort('labAssociationType')}>
                  <Translate contentKey="tinyMedicRestApp.tenantAssociation.labAssociationType">Lab Association Type</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('labAssociationType')} />
                </th>
                <th className="hand" onClick={sort('tenantType')}>
                  <Translate contentKey="tinyMedicRestApp.tenantAssociation.tenantType">Tenant Type</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('tenantType')} />
                </th>
                <th className="hand" onClick={sort('otherTenantType')}>
                  <Translate contentKey="tinyMedicRestApp.tenantAssociation.otherTenantType">Other Tenant Type</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('otherTenantType')} />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.tenantAssociation.tenant">Tenant</Translate> <FontAwesomeIcon icon="sort" />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.tenantAssociation.associatedTenant">Associated Tenant</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {tenantAssociationList.map((tenantAssociation, i) => (
                <tr key={`entity-${i}`} data-cy="entityTable">
                  <td>
                    <Button tag={Link} to={`/tenant-association/${tenantAssociation.id}`} color="link" size="sm">
                      {tenantAssociation.id}
                    </Button>
                  </td>
                  <td>{tenantAssociation.associatedDate}</td>
                  <td>{tenantAssociation.internal ? 'true' : 'false'}</td>
                  <td>{tenantAssociation.leavingDate}</td>
                  <td>{tenantAssociation.openingTime}</td>
                  <td>{tenantAssociation.closeTime}</td>
                  <td>{tenantAssociation.createdDtm}</td>
                  <td>{tenantAssociation.createdBy}</td>
                  <td>{tenantAssociation.modifiedBy}</td>
                  <td>{tenantAssociation.leftAssociation ? 'true' : 'false'}</td>
                  <td>{tenantAssociation.active ? 'true' : 'false'}</td>
                  <td>{tenantAssociation.cursor}</td>
                  <td>{tenantAssociation.lastUpdated}</td>
                  <td>{tenantAssociation.microSec}</td>
                  <td>{tenantAssociation.timestampEntry}</td>
                  <td>
                    <Translate contentKey={`tinyMedicRestApp.LabServiceType.${tenantAssociation.labAssociationType}`} />
                  </td>
                  <td>
                    <Translate contentKey={`tinyMedicRestApp.EntityType.${tenantAssociation.tenantType}`} />
                  </td>
                  <td>
                    <Translate contentKey={`tinyMedicRestApp.EntityType.${tenantAssociation.otherTenantType}`} />
                  </td>
                  <td>
                    {tenantAssociation.tenant ? (
                      <Link to={`/tenant/${tenantAssociation.tenant.id}`}>{tenantAssociation.tenant.id}</Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td>
                    {tenantAssociation.associatedTenant ? (
                      <Link to={`/tenant/${tenantAssociation.associatedTenant.id}`}>{tenantAssociation.associatedTenant.id}</Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td className="text-end">
                    <div className="btn-group flex-btn-group-container">
                      <Button
                        tag={Link}
                        to={`/tenant-association/${tenantAssociation.id}`}
                        color="info"
                        size="sm"
                        data-cy="entityDetailsButton"
                      >
                        <FontAwesomeIcon icon="eye" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.view">View</Translate>
                        </span>
                      </Button>
                      <Button
                        tag={Link}
                        to={`/tenant-association/${tenantAssociation.id}/edit?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`}
                        color="primary"
                        size="sm"
                        data-cy="entityEditButton"
                      >
                        <FontAwesomeIcon icon="pencil-alt" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.edit">Edit</Translate>
                        </span>
                      </Button>
                      <Button
                        onClick={() =>
                          (window.location.href = `/tenant-association/${tenantAssociation.id}/delete?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`)
                        }
                        color="danger"
                        size="sm"
                        data-cy="entityDeleteButton"
                      >
                        <FontAwesomeIcon icon="trash" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.delete">Delete</Translate>
                        </span>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          !loading && (
            <div className="alert alert-warning">
              <Translate contentKey="tinyMedicRestApp.tenantAssociation.home.notFound">No Tenant Associations found</Translate>
            </div>
          )
        )}
      </div>
      {totalItems ? (
        <div className={tenantAssociationList && tenantAssociationList.length > 0 ? '' : 'd-none'}>
          <div className="justify-content-center d-flex">
            <JhiItemCount page={paginationState.activePage} total={totalItems} itemsPerPage={paginationState.itemsPerPage} i18nEnabled />
          </div>
          <div className="justify-content-center d-flex">
            <JhiPagination
              activePage={paginationState.activePage}
              onSelect={handlePagination}
              maxButtons={5}
              itemsPerPage={paginationState.itemsPerPage}
              totalItems={totalItems}
            />
          </div>
        </div>
      ) : (
        ''
      )}
    </div>
  );
};

export default TenantAssociation;
